import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import loadable from '@loadable/component';
import { styled } from 'linaria/react';
import React, { useContext } from 'react';
import MaxWidth from '../Layout/MaxWidth';
import Spinner from '../ui/Spinner';
// import AddToCartForm from './AddToCart/AddToCartForm';
import AddMultipleToCartForm from './AddToCart/AddMultipleToCartForm';
import { ProductSection } from './ProductInfo';
import ProductInfoAccordion from './ProductInfoAccordion';
import ProductPageLoadingState from './ProductPageLoadingState';
import { Container } from './styledComponents';
import { theme } from '../Theme';
import { Heading3 } from '../ui/text/Heading3';
import { Heading1 } from '../ui/text/Heading1';
import { SmallParagraph } from '../ui/text/SmallParagraph';
import { UspItem, Usps } from './Usps';
import { DynamicCategoryContentRenderer } from '../ContentComponents/DynamicContentRenderer';
import ImageGrid from './Images/ImageGrid';
import ImageGallery from './Images/ImageGallery';
import { StyledBreadcrumbs } from '../ui/StyledBreadcrumbs';
import StyledBadges from '../ui/StyledBages';
import { PushPrice } from '../ui/StyledPrice';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import ProductRecommendations from '../ProductRecommendations';
import { useAddwish } from '@jetshop/flight-addwish';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { RelatedProductsFromCustomFields } from './RelatedProductsFromCustomFields';
import { useIntl } from '@jetshop/intl';
import { RecommendedPrice } from './AddToCart/RecommendedPrice';

const LoadablePackageProduct = loadable(
  () => import('./PackageProduct/NewPackageProduct'),
  {
    fallback: <Spinner />
  }
);

const ProductContainer = styled('div')`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding-top: 20px;

  .product-page-badge {
    margin-bottom: 2rem;
  }

  ${theme.below.lg} {
    padding-top: 0;
    .product-page-badge {
      margin-bottom: 1rem;
    }

    .name-and-price-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 5px;

      .price,
      .new-price {
        font-size: 25px;
        line-height: 25px;
      }

      .content {
        padding: 1px 0;
      }

      .percentage,
      .old-price {
        font-size: 10px;

        line-height: 14px;
      }
    }
  }

  .breadcrumbs-wrapper {
    margin-bottom: 20px;

    ${theme.below.lg} {
      width: 100%;
      overflow-x: scroll;
      overflow-y: hidden;
      margin-bottom: 3rem;

      > div > ul {
        justify-content: center;
      }
    }
  }

  .product-detail-container {
    justify-content: flex-start;
    gap: 60px;

    ${theme.below.lg} {
      gap: 20px;
    }
  }

  header {
    .heading-3 {
      margin-bottom: 12px;
    }

    .heading-1 {
    }

    .small-paragraph {
      color: ${theme.colors.black}66;
    }

    margin-bottom: 1rem;
  }

  .product-details {
    .push-style {
      .price {
        font-size: 30px !important;
      }
    }
    ${theme.above.lg} {
      align-items: flex-start;
    }

    .usp-category-wrapper {
      margin-top: 2rem;
    }
  }

  .images {
    flex: 1 !important;
    width: auto !important;
    ${theme.below.lg} {
      width: 100% !important;
      padding-bottom: 0.5rem;
    }
  }

  .recommendations {
    margin-bottom: 40px;
    width: 100%;
    max-width: ${theme.maxWidth};
    padding: 0 20px;
    margin: auto;
    > section {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }
  }
`;

const Product = ({ result: { data, loading }, product, partialProduct }) => {
  const { selectedChannel } = useContext(ChannelContext);

  const relatedProducts = product?.customFields?.filter(
    customField => customField.key === 'relatedProducts'
  )?.[0]?.listValues;

  const findAlternativesKey = () => {
    switch (selectedChannel?.id) {
      //Sweden
      case 1:
        //testkey
        // return 'k6320475cc8a88762728e35a9';
        return 'k5e30507e7c9ad84cc6405d4a';
      //Finland
      case 2:
        return 'k609e8b374c40ff69efebe504';
      //Danmark
      case 3:
        return 'k609e8b2e4c40ff69efebe11b';
      //.com
      default:
        return 'k5ef079d451639f36f1843d83';
    }
  };
  const alternativesKey = findAlternativesKey();

  const findPurchasedTogether = () => {
    switch (selectedChannel?.id) {
      //Sweden
      case 1:
        //testkey
        // return 'k6320475cc8a88762728e35b8';
        return 'k5e30507e7c9ad84cc6405d40';
      //Finland
      case 2:
        return 'k609e8b384c40ff69efebe52c';
      //Danmark
      case 3:
        return 'k609e8b2f4c40ff69efebe185';
      //.com
      default:
        return 'k5ef079d551639f36f1843d91';
    }
  };
  const purchasedTogetherKey = findPurchasedTogether();
  const { addWishResponseBoxItems } = useAddwish({
    boxes: {
      [alternativesKey]: {
        // hierarchies: [
        //   (data?.route?.parents || [])
        //     ?.reverse()
        //     ?.map(parent => parent?.object?.breadcrumbText)
        // ]
      },
      [purchasedTogetherKey]: {}
    },
    pageLoaded: !loading && product
  });

  /**
   * The isEmpty function checks if an object is empty.
   * @param obj Pass in an object to the function
   * @return True if the object has no properties, and false if the object has at least one property
   * @docauthor Anders Zetterström @ R3
   */
  // function isEmpty(obj) {
  //   return Object.keys(obj).length === 0;
  // }

  const initialVariant = useVariantFromUrl();
  // const inititalVariantStockOK = product?.variants?.values?.find(v => {
  //   return v?.stockStatus?.buyable;
  // });

  const variantHandler = useProductVariants(product, {
    initialVariant,
    // initialVariant: isEmpty(initialVariant)
    //   ? inititalVariantStockOK
    //   : initialVariant,
    preselectCheapest: false
  });

  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;

  const price = useDynamicPrice(product, selectedVariation);
  const translate = useIntl();
  const alternativesString = translate('Alternatives');
  const productsPurchasedTogetherString = translate(
    'Products purchased together'
  );

  if (!product) {
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );
  }

  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);

  return (
    <ProductContainer>
      <Above breakpoint="lg">
        <MaxWidth>
          <div className="breadcrumbs-wrapper">
            <StyledBreadcrumbs
              breadcrumbs={data?.route?.breadcrumbs || []}
              parents={parents}
            />
          </div>
        </MaxWidth>
      </Above>
      <Container className="product-detail-container">
        <ProductSection className="images">
          {product?.images && (
            <Above breakpoint="lg">
              {matches =>
                matches ? (
                  <ImageGrid product={product} />
                ) : (
                  <ImageGallery product={product} />
                )
              }
            </Above>
          )}
        </ProductSection>
        <ProductSection className="details">
          <div className="product-details">
            <header>
              <Below breakpoint="lg">
                <div className="breadcrumbs-wrapper">
                  <StyledBreadcrumbs
                    breadcrumbs={data?.route?.breadcrumbs || []}
                    parents={parents}
                  />
                </div>
              </Below>
              <StyledBadges badges={product?.badges} isProductPage />
              {product?.subName?.length > 0 && (
                <Heading3 text={product?.subName} weight={'bold'} />
              )}
              <Above breakpoint="lg">
                {matches =>
                  matches ? (
                    <>
                      <Heading1
                        text={product?.name}
                        data-testid="product-title"
                      />
                      <SmallParagraph text={product.articleNumber} />
                    </>
                  ) : (
                    <div className="name-and-price-row">
                      <div>
                        <Heading1
                          text={product?.name}
                          data-testid="product-title"
                        />
                        <SmallParagraph text={product.articleNumber} />
                      </div>

                      <div>
                        <PushPrice {...price} />
                        <RecommendedPrice product={product} />
                      </div>
                    </div>
                  )
                }
              </Above>
            </header>
            {product?.isPackage ? (
              <LoadablePackageProduct product={product} />
            ) : (
              <AddMultipleToCartForm
                product={product}
                variant={selectedVariation}
                getMissingOptions={getMissingOptions}
                variantHandler={variantHandler}
              />
            )}
            <div className="usp-category-wrapper">
              <DynamicCategoryContentRenderer
                categoryId={271}
                rendererComponents={{ USPROW: Usps, USPITEM: UspItem }}
              />
            </div>

            <ProductInfoAccordion product={product} />
          </div>
        </ProductSection>
      </Container>
      {relatedProducts && (
        <RelatedProductsFromCustomFields products={relatedProducts} />
      )}
      <ProductRecommendations
        title={{ value: alternativesString }}
        items={addWishResponseBoxItems[alternativesKey].items}
      />
      <ProductRecommendations
        title={{ value: productsPurchasedTogetherString }}
        items={addWishResponseBoxItems[purchasedTogetherKey].items}
      />
    </ProductContainer>
  );
};

export default Product;
